import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CenteredLoader } from "../../CenteredLoader";
import { CenteredNotice } from "../CenteredNotice";
import { DiffViewer } from "./DiffViewer";
import { NoVisibleChangesHint } from "./NoVisibleChangesHint";
import { VisualDiffToolbar } from "./VisualDiffToolbar";
import { VisualDiffTypeRail } from "./VisualDiffTypeRail";
import { useVisualDiff } from "./hooks/useVisualDiff";
import { useVisualDiffFilters } from "./hooks/useVisualDiffFilters";
import { useVisualDiffNavigation } from "./hooks/useVisualDiffNavigation";
import { useVisualDiffOverlay } from "./hooks/useVisualDiffOverlay";
import {
  SINGLE_ITEM_ZOOM_PADDING_PDF_POINTS,
  TYPE_OVERVIEW_ZOOM_PADDING_PDF_POINTS,
} from "./constants";
import {
  buildMarkerNavigationTarget,
  buildRouteNavigationTarget,
} from "./utils";
import type { FloorPlanPanelHandlers } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/types";
import type { VisualDiffTypeEntry } from "../utils/computeVisualDiff";
import type { DiffNavigationTarget, VisualDiffTabProps } from "./types";

export const VisualDiffTab = ({
  beforePlanRecord,
  afterPlanRecord,
}: VisualDiffTabProps) => {
  const { t } = useTranslation();

  const viewerRef = useRef<FloorPlanPanelHandlers>(null);

  const { afterPlan, visualDiff, isLoading, isError } = useVisualDiff({
    beforePlanRecord,
    afterPlanRecord,
  });

  const {
    activeKind,
    visibleStatuses,
    selectedTypeKey,
    selectKind,
    toggleStatus,
    selectTypeKey,
    clearTypeSelection,
  } = useVisualDiffFilters();

  const {
    visibleRoutes,
    detectedObjects,
    circuitPaths,
    countsByStatus,
    railEntries,
    isPlanOverlayEmpty,
  } = useVisualDiffOverlay({
    visualDiff,
    activeKind,
    visibleStatuses,
    selectedTypeKey,
  });

  const {
    navigationTargets,
    selectedObject,
    selectItemId,
    handleAfterNavigate,
  } = useVisualDiffNavigation({
    visualDiff,
    activeKind,
    selectedTypeKey,
    visibleStatuses,
    visibleRoutes,
    detectedObjects,
    viewerRef,
  });

  const handleEntrySelect = useCallback(
    (entry: VisualDiffTypeEntry) => {
      selectTypeKey(entry.key);
      if (entry.zoomTargets.length === 0) return;
      viewerRef.current?.zoomToElements({
        elements: entry.zoomTargets,
        padding: TYPE_OVERVIEW_ZOOM_PADDING_PDF_POINTS,
      });
    },
    [selectTypeKey],
  );

  const handleShowAllSelect = useCallback(() => {
    clearTypeSelection();
    viewerRef.current?.zoomToFit();
  }, [clearTypeSelection]);

  const selectClickedItem = useCallback(
    (typeKey: string, target: DiffNavigationTarget) => {
      selectTypeKey(typeKey);
      selectItemId(target.id);
      viewerRef.current?.zoomToElements({
        elements: target.zoomTargets,
        padding: SINGLE_ITEM_ZOOM_PADDING_PDF_POINTS,
      });
    },
    [selectTypeKey, selectItemId],
  );

  // Clicking the plan opens the item's own type, so the rail and the walk follow what was clicked.
  const handleObjectSelect = useCallback(
    (objectId: string) => {
      const clickedMarker = detectedObjects.find(
        (marker) => marker.id === objectId,
      );
      if (clickedMarker !== undefined) {
        selectClickedItem(
          clickedMarker.typeKey,
          buildMarkerNavigationTarget(clickedMarker),
        );
        return;
      }
      const clickedRoute = visibleRoutes.find(
        (diffRoute) => diffRoute.route.id === objectId,
      );
      if (clickedRoute === undefined) return;
      selectClickedItem(
        clickedRoute.typeKey,
        buildRouteNavigationTarget(clickedRoute),
      );
    },
    [detectedObjects, visibleRoutes, selectClickedItem],
  );

  if (isLoading) return <CenteredLoader />;

  if (isError || visualDiff === null || afterPlan === null) {
    return (
      <CenteredNotice
        message={t("smartSurveyor.planComparison.quantities.loadFailed")}
      />
    );
  }

  if (!visualDiff.isPageFormatMatching) {
    return (
      <CenteredNotice
        message={t("smartSurveyor.planComparison.visual.pageFormatMismatch")}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 gap-4">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
        <VisualDiffToolbar
          activeKind={activeKind}
          onKindChange={selectKind}
          countsByStatus={countsByStatus}
          visibleStatuses={visibleStatuses}
          onStatusToggle={toggleStatus}
        />
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200">
          <DiffViewer
            ref={viewerRef}
            afterPlan={afterPlan}
            afterPlanName={afterPlanRecord.name}
            detectedObjects={detectedObjects}
            circuitPaths={circuitPaths}
            selectedObject={selectedObject}
            onSelectObjectId={handleObjectSelect}
            navigationObjects={navigationTargets}
            isObjectNavigationVisible={selectedTypeKey !== null}
            onAfterNavigate={handleAfterNavigate}
          />
          {isPlanOverlayEmpty && <NoVisibleChangesHint />}
        </div>
      </div>
      <VisualDiffTypeRail
        entries={railEntries}
        selectedTypeKey={selectedTypeKey}
        onEntrySelect={handleEntrySelect}
        onShowAllSelect={handleShowAllSelect}
        isRemovedHintVisible={countsByStatus.removed > 0}
        className="w-64 flex-shrink-0 lg:w-72"
      />
    </div>
  );
};
